/**
 * 認證管理 Hook
 * 封裝 token 獲取、刷新、手動登入邏輯
 */

import { useState, useCallback, useRef } from 'react';
import { loginWithMsal, getCurrentAccount, getMsalInstance, isTokenExpired } from '../services/authService';

export const useAuth = () => {
  const [needsManualLogin, setNeedsManualLogin] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState(null);
  
  const tokenRef = useRef(null);
  const tokenPromiseRef = useRef(null);

  /**
   * 獲取訪問令牌
   * @param {Object} options - 選項
   * @param {boolean} options.refresh - 是否強制刷新
   * @param {boolean} options.useManualLogin - 是否使用手動登入
   * @returns {Promise<string|null>} 訪問令牌或 null
   */
  const getAccessToken = useCallback(async (options = { refresh: false, useManualLogin: false }) => {
    // 如果使用手動登入，直接使用 MSAL
    if (options.useManualLogin) {
      const msalToken = await loginWithMsal();
      if (msalToken) {
        tokenRef.current = msalToken;
        setNeedsManualLogin(false);
        setLoginError(null);
        return msalToken;
      }
      return null;
    }

    // 如果有緩存的 token 且不需要刷新，直接返回
    if (tokenRef.current && !options.refresh) {
      if (!isTokenExpired(tokenRef.current)) {
        return tokenRef.current;
      } else {
        console.log("token過期，自動更新");
        tokenRef.current = null;
      }
    }

    // 如果已經有一個 token 請求在進行中，等待它完成
    if (tokenPromiseRef.current) {
      try {
        return await tokenPromiseRef.current;
      } catch (error) {
        // 如果等待的請求失敗，繼續執行新的請求
        console.log("等待中的 token 請求失敗，開始新的請求");
      }
    }

    // 創建新的 token 請求 Promise
    tokenPromiseRef.current = (async () => {
      try {
        const token = await Office.auth.getAccessToken({ allowSignInPrompt: true });
        tokenRef.current = token;
        tokenPromiseRef.current = null; // 清除 Promise 引用
        setNeedsManualLogin(false);
        setLoginError(null);
        return token;
      } catch (error) {
        tokenRef.current = null;
        tokenPromiseRef.current = null; // 清除 Promise 引用
        console.error("Error getting access token:", error);
        
        // 處理錯誤代碼 13008：增益集已經要求存取權杖
        if (error.code === 13008) {
          console.log("檢測到並發 token 請求，等待現有請求完成...");
          // 等待一小段時間後重試
          await new Promise(resolve => setTimeout(resolve, 100));

          // 如果 tokenRef 已經有值（其他請求成功了），返回它
          if (tokenRef.current) {
            return tokenRef.current;
          }
          // SSO 失敗，需要手動登入
          setNeedsManualLogin(true);
          return null;
        }

        // If interactive required, we might want to try one more time with allowSignInPrompt
        if (error.code === 13003 || error.code === 5001 || error.code === 13006) {
          try {
            const token = await Office.auth.getAccessToken({ allowSignInPrompt: true });
            tokenRef.current = token;
            tokenPromiseRef.current = null; // 清除 Promise 引用
            setNeedsManualLogin(false);
            setLoginError(null);
            return token;
          } catch (retryError) {
            tokenPromiseRef.current = null; // 清除 Promise 引用
            console.error("Retry error getting access token:", retryError);
            // SSO 失敗，需要手動登入
            setNeedsManualLogin(true);
            return null;
          }
        }
        
        // 其他錯誤也標記為需要手動登入
        setNeedsManualLogin(true);
        return null;
      }
    })();

    return await tokenPromiseRef.current;
  }, []);

  /**
   * 手動登入處理函數
   */
  const handleManualLogin = useCallback(async () => {
    setIsLoggingIn(true);
    setLoginError(null);
    
    try {
      // 檢查 MSAL 是否已配置
      const msal = getMsalInstance();
      if (!msal || !process.env.SSO_ID) {
        setLoginError("MSAL 未正確配置，請檢查環境變數 SSO_ID");
        setIsLoggingIn(false);
        return;
      }

      const token = await loginWithMsal();
      if (token) {
        // 登入成功，儲存 token
        tokenRef.current = token;
        setNeedsManualLogin(false);
        setLoginError(null);
        return token;
      } else {
        setLoginError("登入失敗");
        setNeedsManualLogin(true);
      }
    } catch (error) {
      console.error("手動登入錯誤:", error);
      setLoginError("登入錯誤");
      setNeedsManualLogin(true);
    } finally {
      setIsLoggingIn(false);
    }
  }, []);

  /**
   * 初始化認證（嘗試 SSO，失敗則檢查 MSAL）
   */
  const initializeAuth = useCallback(async () => {
    const token = await getAccessToken();
    if (token) {
      return token;
    } else {
      // 如果 SSO 失敗，檢查是否有 MSAL 帳戶
      const msalAccount = getCurrentAccount();
      if (msalAccount) {
        // 如果有 MSAL 帳戶，嘗試靜默獲取 token
        try {
          const msalToken = await loginWithMsal();
          if (msalToken) {
            tokenRef.current = msalToken;
            setNeedsManualLogin(false);
            return msalToken;
          } else {
            setNeedsManualLogin(true);
          }
        } catch (error) {
          console.error("MSAL 靜默登入失敗:", error);
          setNeedsManualLogin(true);
        }
      } else {
        setNeedsManualLogin(true);
      }
    }
    return null;
  }, [getAccessToken]);

  return {
    getAccessToken,
    handleManualLogin,
    initializeAuth,
    needsManualLogin,
    isLoggingIn,
    loginError,
    tokenRef,
  };
};

